import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import {
  Briefcase,
  User,
  Newspaper,
  ExternalLink,
  CheckCheck,
  Bell,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useContactUpdates,
  useMarkUpdateRead,
  useMarkAllUpdatesRead,
} from '@/hooks/use-contact-updates'
import type { ContactUpdate } from '@/lib/types'

const typeIcons: Record<string, typeof Briefcase> = {
  job_change: Briefcase,
  profile_update: User,
  company_news: Newspaper,
  social_post: Newspaper,
}

const typeColors: Record<string, string> = {
  job_change: 'text-lilac-foreground dark:text-lilac bg-lilac-soft',
  profile_update: 'text-coral bg-coral-soft',
  company_news: 'text-chartreuse-foreground dark:text-chartreuse bg-chartreuse-soft',
  social_post: 'text-foreground bg-sand',
}

function UpdateCard({
  update,
  onMarkRead,
}: {
  update: ContactUpdate
  onMarkRead: (id: string) => void
}) {
  const Icon = typeIcons[update.updateType] || User
  const colorClass = typeColors[update.updateType] || 'text-muted-foreground bg-muted'

  return (
    <div
      className={cn(
        'flex gap-3 rounded-xl border p-4 transition-colors',
        !update.read ? 'bg-card border-primary/20' : 'bg-card/50 border-border opacity-75',
      )}
    >
      <div
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          colorClass,
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className={cn('text-sm font-medium', !update.read && 'font-semibold')}>
              {update.title}
            </p>
            {update.summary && (
              <p className="text-muted-foreground mt-0.5 text-xs whitespace-pre-line">
                {update.summary}
              </p>
            )}
          </div>
          {!update.read && (
            <button
              onClick={() => onMarkRead(update.id)}
              className="text-muted-foreground hover:text-foreground shrink-0 p-1"
              title="Mark as read"
            >
              <CheckCheck className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="mt-2 flex items-center gap-3 text-xs">
          {update.contactName && (
            <Link
              to={`/contacts/${update.contactId}`}
              className="text-primary font-medium hover:underline"
            >
              {update.contactName}
            </Link>
          )}
          <span className="text-muted-foreground">
            {formatDistanceToNow(new Date(update.detectedAt), { addSuffix: true })}
          </span>
          <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[10px] uppercase">
            {update.source}
          </span>
          {update.sourceUrl && (
            <a
              href={update.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground"
            >
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

export default function UpdatesPage() {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [showUnreadOnly, setShowUnreadOnly] = useState(false)
  const { data, isLoading } = useContactUpdates({
    unreadOnly: showUnreadOnly,
    page,
    limit: 20,
  })
  const markRead = useMarkUpdateRead()
  const markAllRead = useMarkAllUpdatesRead()

  const totalPages = data ? Math.ceil(data.total / 20) : 0

  return (
    <div className="mx-auto max-w-2xl px-4 pt-6 pb-24">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="text-primary h-5 w-5" />
          <h1 className="text-xl font-bold">{t('updates.title', 'Contact Updates')}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setShowUnreadOnly(!showUnreadOnly)
              setPage(1)
            }}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
              showUnreadOnly
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
          >
            {t('updates.unreadOnly', 'Unread only')}
          </button>
          <button
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            className="text-muted-foreground hover:text-foreground rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
          >
            <CheckCheck className="mr-1 inline-block h-3.5 w-3.5" />
            {t('updates.markAllRead', 'Mark all read')}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="border-primary h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : !data?.items.length ? (
        <div className="text-muted-foreground py-12 text-center text-sm">
          <Bell className="mx-auto mb-2 h-8 w-8 opacity-40" />
          <p>{t('updates.empty', 'No updates yet')}</p>
          <p className="mt-1 text-xs">
            {t(
              'updates.emptyHint',
              'Updates appear when your contacts change jobs, companies, or update their profiles.',
            )}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {data.items.map((update) => (
              <UpdateCard
                key={update.id}
                update={update}
                onMarkRead={(id) => markRead.mutate(id)}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-4">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="text-muted-foreground hover:text-foreground disabled:opacity-30"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-muted-foreground text-sm">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="text-muted-foreground hover:text-foreground disabled:opacity-30"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
